import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Type, Eye, EyeOff } from "lucide-react";

const SECTION_KEYS = [
  "permission_to_record", "permission_to_use_story", "editing",
  "compensation", "responsibility", "liability",
  "privacy_preference", "sensitive_topics", "optional_questions", "emergency_contact",
];

const FIELD_LABELS = {
  phone: "Phone Number",
  city: "City",
  state: "State",
  date: "Date",
  opt_receive_copy: "Would you like to receive a copy of your interview?",
  opt_notify_live: "Would you like to be notified when your episode goes live?",
  opt_future_projects: "Would you like Mickey to contact you about future projects?",
  emergency_name: "Emergency Contact — Name",
  emergency_phone: "Emergency Contact — Phone",
  emergency_relationship: "Emergency Contact — Relationship",
};

export default function GuestReleaseFormSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await http.get("/guest-release/admin/form-settings");
      setSettings(data.settings);
    } catch { toast.error("Failed to load form settings"); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/guest-release/admin/form-settings", settings);
      toast.success("Form settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all form settings to defaults? This cannot be undone.")) return;
    try {
      await http.post("/guest-release/admin/form-settings/reset");
      toast.success("Settings reset to defaults");
      setLoading(true);
      load();
    } catch { toast.error("Reset failed"); }
  };

  const toggleSection = (key) => {
    setSettings(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: { ...prev.sections[key], enabled: !prev.sections[key].enabled }
      }
    }));
  };

  const updateSectionText = (key, text) => {
    setSettings(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: { ...prev.sections[key], text }
      }
    }));
  };

  const updateSectionTitle = (key, title) => {
    setSettings(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: { ...prev.sections[key], title }
      }
    }));
  };

  const toggleField = (key) => {
    setSettings(prev => ({
      ...prev,
      fields: { ...prev.fields, [key]: !prev.fields[key] }
    }));
  };

  if (loading || !settings) return <div className="text-center py-12 text-zinc-500 text-sm animate-pulse">Loading form settings...</div>;

  return (
    <div className="space-y-6" data-testid="form-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-[#F4F1EA] font-display text-lg">Form Editor</h3>
          <p className="text-zinc-500 text-xs font-ui">Toggle sections, edit text, and control which fields appear</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-[#F4F1EA] transition-colors"
            data-testid="reset-settings">
            <RotateCcw size={13} /> Reset Defaults
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg bg-[#C9A055] text-black font-ui tracking-wider uppercase hover:bg-[#d4af37] transition-colors disabled:opacity-50"
            data-testid="save-settings">
            <Save size={13} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Header & Intro */}
      <div className="border border-white/10 rounded-xl bg-[#0a0a0a] p-5 space-y-4">
        <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#C9A055]/70 font-ui">Header & Introduction</h4>
        <div>
          <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">Subtitle</label>
          <input value={settings.header_subtitle || ""} onChange={e => setSettings(p => ({ ...p, header_subtitle: e.target.value }))}
            className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-2.5 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
            data-testid="edit-subtitle" />
        </div>
        <div>
          <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">Introduction Text</label>
          <textarea rows={3} value={settings.intro_text || ""} onChange={e => setSettings(p => ({ ...p, intro_text: e.target.value }))}
            className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-2.5 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors resize-none font-ui"
            data-testid="edit-intro" />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#C9A055]/70 font-ui mb-2">Sections</h4>
        {SECTION_KEYS.map((key, i) => {
          const section = settings.sections?.[key];
          if (!section) return null;
          const isOpen = expanded === key;
          const num = i < 7 ? i + 1 : null;

          return (
            <div key={key} className="border border-white/10 rounded-xl bg-[#0a0a0a] overflow-hidden" data-testid={`section-setting-${key}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => toggleSection(key)} className="flex-shrink-0" data-testid={`toggle-${key}`}>
                    {section.enabled ? (
                      <ToggleRight size={24} className="text-emerald-500" />
                    ) : (
                      <ToggleLeft size={24} className="text-zinc-600" />
                    )}
                  </button>
                  <span className={`text-sm font-ui truncate ${section.enabled ? "text-[#F4F1EA]" : "text-zinc-600 line-through"}`}>
                    {num ? `${num}. ` : ""}{section.title}
                  </span>
                </div>
                <button onClick={() => setExpanded(isOpen ? null : key)} className="p-1 text-zinc-500 hover:text-[#F4F1EA] transition-colors"
                  data-testid={`expand-${key}`}>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {isOpen && (
                <div className="border-t border-white/[0.06] px-4 py-4 space-y-4">
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">Section Title</label>
                    <input value={section.title || ""} onChange={e => updateSectionTitle(key, e.target.value)}
                      className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-2.5 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
                      data-testid={`edit-title-${key}`} />
                  </div>
                  {section.text !== undefined && (
                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">Section Text</label>
                      <textarea rows={4} value={section.text || ""} onChange={e => updateSectionText(key, e.target.value)}
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-2.5 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors resize-none font-ui"
                        data-testid={`edit-text-${key}`} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Individual Fields */}
      <div className="border border-white/10 rounded-xl bg-[#0a0a0a] p-5 space-y-3">
        <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#C9A055]/70 font-ui mb-2">Individual Fields</h4>
        <p className="text-zinc-500 text-[10px] font-ui mb-3">Toggle individual form fields on or off. Name, Email, Signature, and Final Agreement are always required.</p>
        {Object.entries(FIELD_LABELS).map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer group" data-testid={`field-toggle-${key}`}>
            <button onClick={() => toggleField(key)} className="flex-shrink-0">
              {settings.fields?.[key] !== false ? (
                <Eye size={18} className="text-emerald-500" />
              ) : (
                <EyeOff size={18} className="text-zinc-600" />
              )}
            </button>
            <span className={`text-sm font-ui ${settings.fields?.[key] !== false ? "text-[#F4F1EA]" : "text-zinc-600 line-through"}`}>
              {label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
